// WebSocket signalling channel to the PassCall relay Worker (see signal/src/index.js).
// Replaces the Firestore Rooms documents: offer, answer and ICE candidates travel
// through a Durable Object in memory and are never written anywhere.

import { SIGNAL_URL } from "./signalConfig.js";

const CONNECT_TIMEOUT_MS = 4000;

export function signalingEnabled() {
  return typeof SIGNAL_URL === "string" && /^wss?:\/\//.test(SIGNAL_URL);
}

/**
 * Open a channel for roomId as the signed-in user. Resolves once the relay has
 * accepted the socket, or rejects on timeout/refusal so the caller can fall back.
 */
export async function openSignalChannel(roomId, user) {
  const idToken = await user.getIdToken();
  const url = `${SIGNAL_URL.replace(/\/$/, "")}/room/${encodeURIComponent(roomId)}?token=${encodeURIComponent(idToken)}`;
  const listeners = new Set();
  const closeListeners = new Set();
  let socket;
  const ready = new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      try { socket.close(); } catch (e) { /* ignore */ }
      reject(new Error("signalling relay did not answer in time"));
    }, CONNECT_TIMEOUT_MS);
    socket = new WebSocket(url);
    socket.onopen = () => { /* wait for the relay's "ready" message */ };
    socket.onerror = () => { clearTimeout(timer); reject(new Error("signalling relay refused the connection")); };
    socket.onclose = () => { closeListeners.forEach((fn) => fn()); };
    socket.onmessage = (event) => {
      let message;
      try { message = JSON.parse(event.data); } catch (e) { return; }
      if (message && message.type === "ready") {
        clearTimeout(timer);
        resolve();
        return;
      }
      listeners.forEach((fn) => fn(message));
    };
  });
  await ready;
  return {
    send(message) { try { socket.send(JSON.stringify(message)); } catch (e) { /* closed */ } },
    onMessage(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    onClose(fn) { closeListeners.add(fn); return () => closeListeners.delete(fn); },
    close() { try { socket.close(1000, "done"); } catch (e) { /* ignore */ } },
  };
}
